import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { ClientOnly, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ContactInfo } from './contact-info';
import { ResumeEducation } from './resume-education';
import { ResumeExperience } from './resume-experience';
import { ResumeRecognition } from './resume-recognition';
import { ResumeSkills } from './resume-skills';

type ResumeRole = 'fe' | 'be' | 'fs' | 'sd' | 'wd' | 'se';

const roleByAs = {
	fe: m.resume_role_fe,
	be: m.resume_role_be,
	fs: m.resume_role_fs,
	sd: m.resume_role_sd,
	wd: m.resume_role_wd,
	se: m.resume_role_se,
} satisfies Record<ResumeRole, () => string>;

const bioByAs = {
	fe: m.resume_bio_fe,
	be: m.resume_bio_be,
	fs: m.resume_bio_fs,
	sd: m.resume_bio_sd,
	wd: m.resume_bio_wd,
	se: m.resume_bio_se,
} satisfies Record<ResumeRole, () => string>;

function SectionTitle({ tag, title }: { tag: string; title: string }) {
	return (
		<div className="mb-5">
			<span className="mb-1 block font-mono text-3xs text-muted-foreground uppercase tracking-[0.18em]">
				{tag}
			</span>
			<h2 className="border-border border-b pb-2 font-bold text-base text-foreground tracking-tight">
				{title}
			</h2>
		</div>
	);
}

type ResumeProps = React.ComponentPropsWithRef<'div'>;

export function Resume({ className, ...props }: ResumeProps) {
	const { as, useColor } = useSearch({ from: '/_main/resume/' });

	const { role, bio } = useMemo(
		() => ({ role: roleByAs[as](), bio: bioByAs[as]() }),
		[as],
	);

	return (
		<main
			className={cn('select-text pt-10', !useColor && 'resume-white-theme')}
		>
			<div
				id="resume-content"
				className={cn(
					'mx-auto bg-card font-futura text-foreground shadow-2xl',
					className,
				)}
				style={{ maxWidth: '794px', minHeight: '1123px' }}
				{...props}
			>
				<div className="border-border border-b px-6 pt-10 pb-6 md:px-10 print:px-10">
					<section className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
						<div>
							<p className="mb-2 font-mono text-3xs text-muted-foreground uppercase tracking-[0.18em]">
								{role}
							</p>
							<h1 className="mb-3 font-bold text-4xl text-foreground leading-none tracking-tighter sm:text-5xl">
								Sohan R. Emon
							</h1>
							<p className="max-w-sm text-muted-foreground text-sm leading-relaxed">
								{bio}
							</p>
						</div>
						<ContactInfo className="min-w-56" />
					</section>
				</div>

				<div className="flex max-md:flex-col print:flex-row">
					<div className="shrink-0 space-y-8 border-border border-s px-6 py-8 md:w-52 print:w-52">
						<section>
							<SectionTitle
								tag={m.resume_tag_academic()}
								title={m.resume_title_education()}
							/>
							<ResumeEducation />
						</section>
						<section>
							<SectionTitle
								tag={m.resume_tag_recognition()}
								title={m.resume_title_awards()}
							/>
							<ResumeRecognition />
						</section>
						<section>
							<SectionTitle
								tag={m.resume_tag_communication()}
								title={m.resume_title_languages()}
							/>
							<div className="space-y-1">
								{[
									['English', 'Professional'],
									['Bengali', 'Native'],
								].map(([lang, level]) => (
									<div key={lang} className="flex items-center justify-between">
										<span className="font-mono text-2xs text-foreground">
											{lang}
										</span>
										<span className="font-mono text-3xs text-muted-foreground">
											{level}
										</span>
									</div>
								))}
							</div>
						</section>
						<section>
							<SectionTitle
								tag={m.resume_tag_stack()}
								title={m.resume_title_skills()}
							/>
							<ResumeSkills />
						</section>
					</div>

					<div className="flex-1 px-8 py-8">
						<SectionTitle
							tag={m.resume_tag_experience()}
							title={m.resume_title_experience()}
						/>
						<ResumeExperience />
						<br />
						<section>
							<SectionTitle
								tag={m.resume_tag_works()}
								title={m.resume_title_works()}
							/>
							<p className="text-xs">
								{m.resume_portfolio_go()}
								<a
									href="https://sohanscript.web.app/works"
									className="text-primary hover:underline"
								>
									{m.resume_portfolio_link()}
								</a>
								.
							</p>
						</section>
					</div>
				</div>

				<footer className="flex flex-col items-center justify-between gap-2 border-border border-t px-6 py-4 sm:flex-row">
					<span className="font-mono text-2xs text-muted-foreground tracking-widest">
						<Iconify
							icon="lucide:copyright"
							className="inline-block size-3 align-top"
						/>{' '}
						<ClientOnly>{new Date().getFullYear()}</ClientOnly> SOHAN R. EMON
					</span>
					<span className="font-mono text-3xs text-muted-foreground tracking-widest">
						FARIDPUR, DHAKA, BANGLADESH
					</span>
				</footer>
			</div>
		</main>
	);
}
